/**
 * Burt Chat Utilities - Firestore integration for conversation history
 * 
 * Features:
 * - Save/load chat sessions
 * - Auto-generate titles
 * - Group by date
 * - Search conversations
 */

import { 
  collection, 
  addDoc, 
  getDocs, 
  getDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  updateDoc,
  doc,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Auto-generate chat title from first message
 * @param {string} firstMessage - First user message
 * @returns {string} - Short title (max 50 chars)
 */
export function generateChatTitle(firstMessage) {
  if (!firstMessage) return 'New Chat';
  
  // Take first 50 chars, add ellipsis if truncated
  const truncated = firstMessage.length > 50 
    ? firstMessage.substring(0, 47) + '...' 
    : firstMessage;
  
  return truncated;
}

/**
 * Group conversations by date
 * @param {Array} sessions - Array of chat sessions
 * @returns {Object} - Grouped sessions { Today: [...], Yesterday: [...], etc. }
 */
export function groupSessionsByDate(sessions) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const lastWeek = new Date(today);
  lastWeek.setDate(lastWeek.getDate() - 7);
  
  const groups = {
    Today: [],
    Yesterday: [],
    'This Week': [],
    Older: []
  };
  
  sessions.forEach(session => {
    const lastActive = session.lastActiveAt?.toDate?.() || new Date(session.lastActiveAt);
    const sessionDate = new Date(lastActive.getFullYear(), lastActive.getMonth(), lastActive.getDate());
    
    if (sessionDate.getTime() === today.getTime()) {
      groups.Today.push(session);
    } else if (sessionDate.getTime() === yesterday.getTime()) {
      groups.Yesterday.push(session);
    } else if (sessionDate >= lastWeek) {
      groups['This Week'].push(session);
    } else {
      groups.Older.push(session);
    }
  });
  
  // Remove empty groups
  Object.keys(groups).forEach(key => {
    if (groups[key].length === 0) delete groups[key];
  });
  
  return groups;
}

/**
 * Save chat session to Firestore
 * @param {string} familyId 
 * @param {string} userId 
 * @param {Array} messages - Array of message objects
 * @param {string} sessionId - Existing session ID (or null for new)
 * @returns {string} - Session ID
 */
export async function saveChatSession(familyId, userId, messages, sessionId = null) {
  if (!familyId || !userId || !messages || messages.length === 0) {
    throw new Error('Missing required parameters for saveChatSession');
  }
  
  const firstMessage = messages.find(m => m.type === 'user');
  const title = generateChatTitle(firstMessage?.content || 'New Chat');
  
  const sessionData = {
    familyId,
    userId,
    title,
    messages,
    lastActiveAt: serverTimestamp(),
    messageCount: messages.length
  };
  
  try {
    if (sessionId) {
      // Update existing session
      const sessionRef = doc(db, `families/${familyId}/chatSessions/${sessionId}`);
      await updateDoc(sessionRef, sessionData);
      return sessionId;
    } else {
      // Create new session
      sessionData.createdAt = serverTimestamp();
      const sessionsRef = collection(db, `families/${familyId}/chatSessions`);
      const docRef = await addDoc(sessionsRef, sessionData);
      return docRef.id;
    }
  } catch (error) {
    console.error('Error saving chat session:', error);
    throw error;
  }
}

/**
 * Load past conversations for a user
 * @param {string} familyId 
 * @param {string} userId 
 * @param {number} limitCount - Max sessions to load (default 20)
 * @returns {Array} - Array of chat sessions (without full messages to save bandwidth)
 */
export async function loadChatSessions(familyId, userId, limitCount = 20) {
  if (!familyId || !userId) {
    return [];
  }
  
  try {
    const sessionsRef = collection(db, `families/${familyId}/chatSessions`);
    const q = query(
      sessionsRef,
      where('userId', '==', userId),
      orderBy('lastActiveAt', 'desc'),
      limit(limitCount)
    );
    
    const snapshot = await getDocs(q);
    const sessions = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return sessions;
  } catch (error) {
    console.error('Error loading chat sessions:', error);
    return [];
  }
}

/**
 * Load a specific conversation with full messages
 * @param {string} familyId 
 * @param {string} sessionId 
 * @returns {Object|null} - Session with messages or null if not found
 */
export async function loadChatSession(familyId, sessionId) {
  if (!familyId || !sessionId) {
    return null;
  }
  
  try {
    const sessionRef = doc(db, `families/${familyId}/chatSessions/${sessionId}`);
    const docSnap = await getDoc(sessionRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error loading chat session:', error);
    return null;
  }
}

/**
 * Search conversations by title or message content
 * @param {string} familyId 
 * @param {string} userId 
 * @param {string} searchTerm 
 * @returns {Array} - Matching sessions
 */
export async function searchChatSessions(familyId, userId, searchTerm) {
  if (!familyId || !userId || !searchTerm) {
    return [];
  }
  
  try {
    // Load all sessions (Firestore doesn't support text search)
    const sessions = await loadChatSessions(familyId, userId, 100);
    
    // Filter client-side
    const term = searchTerm.toLowerCase();
    const matches = sessions.filter(session => {
      // Search in title
      if (session.title?.toLowerCase().includes(term)) return true;
      
      // Search in messages
      if (session.messages?.some(m => m.content?.toLowerCase().includes(term))) return true;
      
      return false;
    });
    
    return matches;
  } catch (error) {
    console.error('Error searching chat sessions:', error);
    return [];
  }
}

/**
 * Delete a chat session
 * @param {string} familyId 
 * @param {string} sessionId 
 */
export async function deleteChatSession(familyId, sessionId) {
  if (!familyId || !sessionId) {
    throw new Error('Missing required parameters for deleteChatSession');
  }
  
  try {
    const sessionRef = doc(db, `families/${familyId}/chatSessions/${sessionId}`);
    await sessionRef.delete();
  } catch (error) {
    console.error('Error deleting chat session:', error);
    throw error;
  }
}
